import { Component, Inject, OnDestroy, OnInit, Optional, QueryList, ViewChildren } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { WarningBoxDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, TimingData, Tools } from "@comunicare/comunicare-helper";
import {
  FHIRHelper,
  IConfigParam,
  IConfiguration,
  IObsDefWithTransOrigin,
  IObservationDefinition,
  IObservationParam,
  IQuestionnaireOrigin,
  IQuestionnaireParam,
  ITiming,
  STATUS_ENTITY,
} from "@comunicare/comunicare-interfaces";
import { TimingEditorComponent } from "@comunicare/comunicare-timing-editor";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PatientService } from "src/app/providers/patient.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";

export interface IDefinitionListItem {
  active: boolean;
  canBeDisabled: boolean;
  originName?: string;
  standaloneId: string; // Obs : loinc — Quest : identifier[0].value
  settingsId: string; // Obs : reference — Quest : identifier[0].value
  displayName?: string;
  obsDef?: IObservationDefinition;
}

export interface IDefinitionParam extends IConfigParam {
  translatedName?: string;
}

@Component({
  selector: "app-definition-list",
  templateUrl: "./definition-list.component.html",
  styleUrls: ["./definition-list.component.scss"],
})
export class DefinitionListComponent implements OnInit, OnDestroy {
  @ViewChildren(TimingEditorComponent) freqEditors: QueryList<TimingEditorComponent>;

  private onDestroy$ = new Subject<void>();
  public pageLoaded = false;
  public isModal = false;
  public context: "Observation" | "Questionnaire";

  public careplanItems: IDefinitionListItem[] = [];
  public activityItems: IDefinitionListItem[] = [];
  public standaloneItems: IDefinitionListItem[] = [];
  public questionnaireItems: IDefinitionListItem[] = []; // for obs questionnaires

  public standaloneChecked: string[] = [];
  public canAddStandalone = false;
  public patientParamIds: string[] = [];

  public canUpdateParamFreq = false;
  public paramsToUpdate: IDefinitionParam = null;
  public frequenciesToUpdate: ITiming[] = null;

  public paramForm = this.fb.group({
    allowUserEdit: [false, Validators.required],
  });

  private patientConfig: IConfiguration;

  constructor(
    private patientService: PatientService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private userService: UserService,
    private sessionService: SessionService,
    private dialog: MatDialog,
    public patientRegistrationService: PatientRegistrationService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { patientId: string; context?: "Observation" | "Questionnaire" },
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.isModal = !!this.data?.patientId;
    this.context = this.data?.context ?? "Observation";
    this.loadItems(true);
    this.loadPatientConfiguration();

    if (this.userService.isAuthorizedSync(null, "dashboard/configurationPatient", "PUT")) {
      this.canAddStandalone = true;
    }
    const freqRoute = this.context === "Observation" ? "dashboard/obsParamsFreqConfig" : "dashboard/questParamsFreqConfig";
    if (this.userService.isAuthorizedSync(null, freqRoute, "PUT")) {
      this.canUpdateParamFreq = true;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private loadItems(firstInit = false): void {
    if (this.context === "Observation") {
      this.loadObservations(firstInit);
    } else {
      this.loadQuestionnaires(firstInit);
    }
  }

  private loadObservations(firstInit: boolean): void {
    this.patientService
      .getPatientObservationDefinitions(this.resolvePatientId(), this.sessionService.userLang)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (obsDef) => {
          this.careplanItems = this.normalizeObsItems(obsDef.careplan);
          this.activityItems = this.normalizeObsItems(obsDef.activity);
          this.standaloneItems = this.normalizeObsItems(obsDef.standalone);
          this.questionnaireItems = this.normalizeObsItems(obsDef.questionnaire);
          if (firstInit) {
            this.initStandaloneChecked();
          }
          this.pageLoaded = true;
        },
        (err) => {
          FileLogger.error("DefinitionListComponent", "loadObservations", err);
          this.onFinish();
        }
      );
  }

  private loadQuestionnaires(firstInit: boolean): void {
    this.patientService
      .getPatientQuestionnaires(this.resolvePatientId(), this.sessionService.userLang)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (quest) => {
          this.careplanItems = this.normalizeQuestItems(quest.careplan);
          this.activityItems = this.normalizeQuestItems(quest.activity);
          this.standaloneItems = this.normalizeQuestItems(quest.standalone);
          this.questionnaireItems = []; // only for observation context
          if (firstInit) {
            this.initStandaloneChecked();
          }
          this.pageLoaded = true;
        },
        (err) => {
          FileLogger.error("DefinitionListComponent", "loadQuestionnaires", err);
          this.onFinish();
        }
      );
  }

  private loadPatientConfiguration(autoOpenItem?: IDefinitionListItem): void {
    this.patientService
      .getPatientConfiguration(this.resolvePatientId())
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        this.patientConfig = config;
        if (this.context === "Observation") {
          this.patientParamIds = config.parameters.observationParams.map((o) => o.reference ?? String(o.type));
          if (autoOpenItem) {
            this.openParamFrequency(autoOpenItem);
          }
        } else {
          this.patientParamIds = config.parameters.questionnaireParams.map((q) => q.identifier.system);
        }
      });
  }

  private normalizeObsItems(items: IObsDefWithTransOrigin[]): IDefinitionListItem[] {
    if (!items) return [];
    return items.map((obs) => ({
      active: obs.active,
      canBeDisabled: obs.canBeDisabled ?? true,
      originName: obs.originName,
      standaloneId: obs.observationDefinition.loinc,
      settingsId: obs.paramRef,
      obsDef: obs.observationDefinition,
    }));
  }

  private normalizeQuestItems(items: IQuestionnaireOrigin[]): IDefinitionListItem[] {
    if (!items) return [];
    return items.map((q) => ({
      active: q.active,
      canBeDisabled: q.canBeDisabled ?? true,
      originName: q.originName,
      standaloneId: q.questionnaire.identifier[0].value,
      settingsId: q.paramRef,
      displayName: q.questionnaire.subjectType,
    }));
  }

  private initStandaloneChecked(): void {
    this.standaloneChecked = this.standaloneItems.filter((item) => item.active).map((item) => item.standaloneId);
  }

  public onStandaloneChange($event: MatCheckboxChange, item: IDefinitionListItem): void {
    if ($event.checked && !this.standaloneChecked.includes($event.source.value)) {
      this.standaloneChecked.push($event.source.value);
    } else if (!$event.checked && this.standaloneChecked.includes($event.source.value)) {
      const index = this.standaloneChecked.findIndex((id) => id === $event.source.value);
      if (index !== -1) {
        this.standaloneChecked.splice(index, 1);
      }
    }

    const activeIds = this.standaloneItems.filter((i) => i.active).map((i) => i.standaloneId);
    if (!Tools.isEqual(this.standaloneChecked, activeIds)) {
      this.paramsToUpdate = null;
      this.executeSave($event.checked ? item : undefined);
    }
  }

  private executeSave(autoOpenItem?: IDefinitionListItem): void {
    this.pageLoaded = false;

    const patientId = this.resolvePatientId();
    const obsIds = this.context === "Observation" ? this.standaloneChecked : undefined;
    const questIds = this.context === "Questionnaire" ? this.standaloneChecked : undefined;

    this.patientService.updatePatientConfiguration(patientId, obsIds, questIds).subscribe(() => {
      this.snackBar.open(`✅ ${this.translateService.instant("common.success")}`, null, {
        duration: 4000,
      });
      this.loadItems(false);
      this.loadPatientConfiguration(autoOpenItem);
    });
  }

  public openParamFrequency(item: IDefinitionListItem): void {
    if (!this.canUpdateParamFreq || !this.patientConfig) {
      FileLogger.error(
        "DefinitionListComponent",
        "openParamFrequency(): Cannot update parameter frequency: no permission or no patient configuration",
        item,
        "none"
      );
      return;
    }

    const resolved = this.resolveParam(item.settingsId, item.standaloneId);
    if (!resolved) {
      FileLogger.warn(
        "DefinitionListComponent",
        "openParamFrequency(): Cannot update parameter frequency: could not resolve parameter",
        item,
        "none"
      );
      return;
    }

    this.paramsToUpdate = resolved;
    this.paramsToUpdate.translatedName = item.obsDef
      ? Tools.getTranslation(item.obsDef.nameTranslation, this.sessionService.userLang, item.obsDef.loinc)
      : item.displayName;

    if (this.paramsToUpdate.frequencies?.length) {
      this.frequenciesToUpdate = Tools.deepCopy(this.paramsToUpdate.frequencies);
    } else if (this.paramsToUpdate.frequency) {
      this.frequenciesToUpdate = [Tools.deepCopy(this.paramsToUpdate.frequency)];
    } else {
      this.frequenciesToUpdate = null;
    }

    this.paramForm.controls.allowUserEdit.setValue(this.paramsToUpdate.entityStatus?.includes(STATUS_ENTITY.LOCKED) ? false : true);

    setTimeout(() => {
      document.getElementById("scrollTop")?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  }

  /**
   *
   * @param id Obs : reference — Quest : identifier[0].value
   * @param id2 Obs : loinc — Quest : not used
   * @returns
   */
  private resolveParam(id: string | null, id2?: string): IDefinitionParam | null {
    const refSplit = id?.split("__");
    if (this.context === "Observation") {
      let found = this.patientConfig.parameters.observationParams.find(
        (o: IObservationParam) => o.reference === id && Tools.observationLoincConversionMap(String(o.type)) === id2
      );
      if (!found && refSplit?.length > 1) {
        found = this.patientConfig.parameters.observationParams.find(
          (o: IObservationParam) => o.reference === refSplit[0] && Tools.observationLoincConversionMap(String(o.type)) === id2
        );
      }
      if (!found) return null;
      return Tools.deepCopy(found) as unknown as IDefinitionParam;
    } else {
      let found = this.patientConfig.parameters.questionnaireParams.find((q: IQuestionnaireParam) => q.identifier.system === id);
      if (!found && refSplit?.length > 1) {
        found = this.patientConfig.parameters.questionnaireParams.find((q: IQuestionnaireParam) => q.identifier.system === refSplit[0]);
      }
      if (!found) return null;
      return Tools.deepCopy(found) as unknown as IDefinitionParam;
    }
  }

  public async saveParamsFreq(): Promise<void> {
    if (!this.paramsToUpdate) return;

    for (const editor of this.freqEditors.toArray()) {
      if (!editor.save()) {
        return;
      }
    }

    if (!this.checkFrequenciesValidity()) {
      this.showFrequencyError();
      return;
    }

    if (this.frequenciesToUpdate?.length > 1) {
      this.paramsToUpdate.frequencies = this.frequenciesToUpdate;
      this.paramsToUpdate.frequency = this.frequenciesToUpdate[0];
    } else if (this.frequenciesToUpdate?.length === 1) {
      this.paramsToUpdate.frequency = this.frequenciesToUpdate[0];
      this.paramsToUpdate.frequencies = null;
    }

    this.pageLoaded = false;

    const entityStatus: STATUS_ENTITY = this.paramForm.controls.allowUserEdit.value === true ? STATUS_ENTITY.ACTIVE : STATUS_ENTITY.LOCKED;

    const patientId = this.resolvePatientId();
    const onSuccess = () => {
      this.snackBar
        .open(`✅ ${this.translateService.instant("common.success")}`, null, { duration: 4000 })
        .afterDismissed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.paramsToUpdate = null;
          this.loadItems();
          this.loadPatientConfiguration();
        });
    };

    if (this.context === "Observation") {
      const param = this.paramsToUpdate as IObservationParam;
      this.patientService
        .updatePatientObsParamsTiming(
          patientId,
          String(param.type),
          param.frequency,
          param.frequencies ?? null,
          entityStatus,
          param.reference
        )
        .subscribe(onSuccess);
    } else {
      const param = this.paramsToUpdate as IQuestionnaireParam;
      this.patientService
        .updatePatientQuestParamsTiming(patientId, param.identifier.system, param.frequency, param.frequencies ?? null, entityStatus)
        .subscribe(onSuccess);
    }
  }

  public addFrequency(): void {
    if (!this.frequenciesToUpdate) return;

    const prev = this.frequenciesToUpdate[this.frequenciesToUpdate.length - 1];
    const start =
      prev.boundsPeriod?.end && moment(prev.boundsPeriod?.end).year() !== 9999
        ? moment(prev.boundsPeriod.end).add(1, "d").startOf("day").format()
        : moment().format();
    const end = moment(start).add(1, "month").endOf("day").format();

    this.frequenciesToUpdate.push({
      boundsPeriod: { start, end },
      count: null,
      endless: false,
      frequency: 1,
      period: 1,
      periodUnits: "d",
      when: null, // this should absolutely be null by default (not undefined) or everything crashes
      timingCode: "",
      duration: 1,
      durationUnit: "M",
    } as ITiming);
  }

  public removeFrequency(index: number): void {
    if (!this.frequenciesToUpdate || this.frequenciesToUpdate.length < 2) return;
    const next = this.frequenciesToUpdate[index + 1];
    this.frequenciesToUpdate.splice(index, 1);
    if (next) {
      TimingData.computeFrequenciesStartEndDates(this.frequenciesToUpdate, this.frequenciesToUpdate[0].boundsPeriod.start);
    }
  }

  public onFinish(): void {
    if (this.patientRegistrationService.isLastStep) {
      this.patientRegistrationService.onFinish();
    } else {
      this.patientRegistrationService.onNextStep$.next();
    }
  }

  public get i18n() {
    return this.context === "Observation"
      ? {
          title: "observation.obsDefinition.title",
          careplanSection: "observation.obsDefinition.careplanObs",
          activitySection: "observation.obsDefinition.activityObs",
          standaloneSection: "observation.obsDefinition.additionalObs",
          noItems: "observation.obsDefinition.noLinkedObs",
          removeError: "observation.obsDefinition.removeError",
        }
      : {
          title: "questionnaire.definition.title",
          careplanSection: "questionnaire.definition.careplanQuest",
          activitySection: "questionnaire.definition.activityQuest",
          standaloneSection: "questionnaire.definition.additionalQuest",
          noItems: "questionnaire.definition.noLinkedQuest",
          removeError: "observation.obsDefinition.removeError",
        };
  }

  private resolvePatientId(): string {
    return this.data?.patientId ?? FHIRHelper.getMainIdentifier(this.patientRegistrationService.patientUser.patient).value;
  }

  private checkFrequenciesValidity(): boolean {
    if (!this.frequenciesToUpdate?.length) {
      return false;
    }
    if (this.frequenciesToUpdate.length === 1) {
      return true;
    }

    let previous: ITiming = null;
    for (const freq of this.frequenciesToUpdate) {
      if (!previous) {
        previous = freq;
        continue;
      }
      if (!previous.boundsPeriod?.end || previous.endless || moment(freq.boundsPeriod.start).isBefore(moment(previous.boundsPeriod.end))) {
        return false;
      }
      previous = freq;
    }
    return true;
  }

  private showFrequencyError(): void {
    this.snackBar.open(`❗${this.translateService.instant("timing.incoherentFrequencies")}`, "OK", {
      duration: 5000,
    });
    const dates = this.frequenciesToUpdate
      .map((f) => `${moment(f.boundsPeriod.start).format("YYYY-MM-DD")} -> ${moment(f.boundsPeriod.end).format("YYYY-MM-DD")}`)
      .join("<br/>");
    this.dialog.open(WarningBoxDialogComponent, {
      data: {
        text: `${this.translateService.instant("timing.incoherentFrequencies")}<br/><br/>${dates}`,
        type: "error",
      },
      disableClose: true,
    });
  }
}
